import type { PersonDetail, ShredReasonCode } from '@sentra/contracts';
import { OPERATOR_SELECTABLE_SHRED_REASONS } from '@sentra/contracts';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/design/components/label';
import { NativeSelect } from '@/design/components/native-select';
import { TypedConfirmDialog } from '@/features/typed-confirm';

/**
 * AD-10's erasure: the one irreversible thing that can be done to a person's record.
 *
 * **The confirmation is the record's OWN short reference**, which the contract demands and which is
 * what makes the act impossible to complete by clicking through a dialog: the person doing it has
 * to have the record in front of them. `P-000123` is on the screen behind this dialog and nowhere
 * else in the operator's head.
 *
 * **The reason is a closed set and comes from the contracts package.** `shred_ledger.reason_code`
 * survives the erasure — it is the only thing that does — and it is what an auditor reads years
 * later to answer "why is this person gone". Free text there would be four spellings of one reason
 * across four operators, and no report could group them. `retention_policy` is in the set and is
 * not in this picker: it is the nightly job's reason, and the type excludes it, so a screen that
 * tried to offer it would not compile.
 *
 * **Nothing is sent until both are given.** The phrase gates the button and the reason gates the
 * submission, which is the negative this dialog exists for: a cancelled erasure leaves no trace at
 * all, because the request that would have left one was never made.
 */
export function EraseDialog({
  person,
  open,
  pending,
  onConfirm,
  onCancel,
}: Readonly<{
  person: PersonDetail;
  open: boolean;
  pending: boolean;
  onConfirm: (reasonCode: ShredReasonCode) => void;
  onCancel: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const reasonId = useId();
  const [reason, setReason] = useState<ShredReasonCode | ''>('');

  return (
    <TypedConfirmDialog
      open={open}
      title={t('person.erase.title', { shortRef: person.shortRef })}
      body={t('person.erase.body')}
      phrase={person.shortRef}
      confirmLabel={t('person.erase.confirm')}
      pending={pending}
      ready={reason !== ''}
      onConfirm={() => {
        if (reason !== '') {
          onConfirm(reason);
        }
      }}
      onCancel={onCancel}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={reasonId}>{t('person.erase.reason')}</Label>
        <NativeSelect
          id={reasonId}
          value={reason}
          data-testid="erase-reason"
          onChange={(event) => {
            const chosen = OPERATOR_SELECTABLE_SHRED_REASONS.find(
              (candidate) => candidate === event.target.value,
            );
            setReason(chosen ?? '');
          }}
        >
          <option value="">{t('person.erase.reasonPlaceholder')}</option>
          {OPERATOR_SELECTABLE_SHRED_REASONS.map((code) => (
            <option key={code} value={code}>
              {t(`person.erase.reason_${code}`)}
            </option>
          ))}
        </NativeSelect>
        {reason === '' && (
          <p className="text-xs text-muted-foreground">{t('person.erase.reasonRequired')}</p>
        )}
      </div>
      <p className="text-sm text-destructive">{t('person.erase.irreversible')}</p>
    </TypedConfirmDialog>
  );
}
